import {
  memberMapResponseSchema,
  type MemberMapArea,
  type MemberMapQuery,
  type MemberMapResponse,
  type MemberOrigin,
} from '@growup/accounts-shared';
import { Button, Card, Checkbox, cx, Field, Notice, Select, Table, type Column } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf } from '../api';
import { ORIGIN_LABEL } from './labels';
import { MAP_VIEWS, MemberMap, type MapView } from './MemberMap';

const LEVELS: Record<MemberMapQuery['level'], string> = {
  all: 'Membros e associados',
  member: 'Só membros',
  associate: 'Só associados',
};

/** A total row: the number, or "menos de N" when it is too small to show. */
export function countLabel(count: number | null, minCount: number): string {
  return count === null ? `menos de ${String(minCount)}` : String(count);
}

const areaColumns: Column<MemberMapArea>[] = [
  {
    key: 'area',
    header: 'Zona',
    cell: (a) => (
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-semibold">{a.label}</span>
        <span className="truncate text-caption text-gu-ink-muted">
          {a.kind === 'cp4' ? `${a.region} · CP ${a.cp4.join(', ')}` : a.region}
        </span>
      </span>
    ),
  },
  { key: 'count', header: 'Pessoas', cell: (a) => a.count, align: 'right' },
];

/**
 * Where members live (RF-A3), to plan events near them. Counts per area only:
 * an area with fewer than `minCount` people joins its district, or only counts
 * in the total, so nobody can be picked out on the map.
 */
export function MapView() {
  const [level, setLevel] = useState<MemberMapQuery['level']>('all');
  const [origin, setOrigin] = useState<MemberOrigin | ''>('');
  const [inactive, setInactive] = useState(true);
  const [view, setView] = useState<MapView>('portugal');
  const [selected, setSelected] = useState<string | null>(null);
  const [data, setData] = useState<MemberMapResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams({ level, inactive: inactive ? 'include' : 'exclude' });
    if (origin) params.set('origin', origin);
    getJson(`/api/members/map?${params.toString()}`, memberMapResponseSchema)
      .then((map) => {
        setError(null);
        setData(map);
        setSelected(null);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [level, origin, inactive]);

  // Imported inactive people are not members yet and all came from the import.
  const canIncludeInactive = level === 'all' && (origin === '' || origin === 'import');
  const select = useCallback((key: string) => setSelected(key), []);
  const mapped = data ? data.total - data.hidden - data.unmapped : 0;

  return (
    <div className="flex flex-col gap-4">
      <Card headingLevel={2} title="Onde estão os membros">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Nível">
            <Select
              value={level}
              onChange={(e) => setLevel(e.target.value as MemberMapQuery['level'])}
            >
              {(Object.keys(LEVELS) as MemberMapQuery['level'][]).map((l) => (
                <option key={l} value={l}>
                  {LEVELS[l]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Origem">
            <Select value={origin} onChange={(e) => setOrigin(e.target.value as MemberOrigin | '')}>
              <option value="">Todas</option>
              {(Object.keys(ORIGIN_LABEL) as MemberOrigin[]).map((o) => (
                <option key={o} value={o}>
                  {ORIGIN_LABEL[o]}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-end pb-2">
            <Checkbox
              label="Incluir importados inativos"
              checked={inactive && canIncludeInactive}
              disabled={!canIncludeInactive}
              onChange={(e) => setInactive(e.target.checked)}
            />
          </div>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {data ? (
          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Pessoas" value={data.total} />
            <Stat label="No mapa" value={mapped} />
            <Stat label={`Em zonas com menos de ${String(data.minCount)}`} value={data.hidden} />
            <Stat label="Sem localização" value={data.unmapped} />
          </dl>
        ) : null}
      </Card>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <Card headingLevel={2} title="Mapa">
          <nav className="mb-3 flex flex-wrap gap-2" aria-label="Zona do mapa">
            {(Object.keys(MAP_VIEWS) as MapView[]).map((v) => (
              <Button
                key={v}
                size="sm"
                variant={view === v ? 'primary' : 'secondary'}
                aria-pressed={view === v}
                onClick={() => {
                  setSelected(null);
                  setView(v);
                }}
              >
                {MAP_VIEWS[v].label}
              </Button>
            ))}
          </nav>
          <MemberMap areas={data?.areas ?? []} view={view} selected={selected} onSelect={select} />
          <p className="mt-3 text-caption text-gu-ink-muted">
            Cada círculo é uma zona de código postal (CP4), maior onde há mais pessoas. Zonas com
            menos de {data?.minCount ?? 3} pessoas juntam-se em &quot;Outras zonas&quot; do
            distrito, na sede de distrito; se mesmo assim forem poucas, só contam no total. Moradas
            fora de Portugal ficam no centro do país.
          </p>
        </Card>

        <div className="flex flex-col gap-4">
          <Card headingLevel={2} title="Zonas">
            <Table
              caption="Zonas com mais membros"
              columns={areaColumns}
              rows={data?.areas ?? []}
              rowKey={(a) => a.key}
              empty={data ? 'Nenhuma zona com pessoas suficientes.' : 'A carregar…'}
              onRowClick={(a) => setSelected(a.key)}
              className="max-h-[24rem] overflow-y-auto"
            />
          </Card>
          {data ? (
            <Card headingLevel={2} title="Totais">
              <Totals title="Por distrito" rows={data.districts} minCount={data.minCount} />
              <Totals title="Por país" rows={data.countries} minCount={data.minCount} />
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Totals({
  title,
  rows,
  minCount,
}: {
  title: string;
  rows: MemberMapResponse['districts'];
  minCount: number;
}) {
  if (rows.length === 0) return null;
  return (
    <section className="mb-4 last:mb-0">
      <h3 className="mb-2 font-sub text-label font-bold uppercase text-gu-ink-muted">{title}</h3>
      <ul className="flex flex-col gap-1">
        {rows.map((r) => (
          <li key={r.name} className="flex justify-between gap-2">
            <span>{r.name}</span>
            <span className={cx(r.count === null && 'text-gu-ink-muted')}>
              {countLabel(r.count, minCount)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1 border-l-2 border-gu-accent pl-3">
      <dt className="text-caption text-gu-ink-muted">{label}</dt>
      <dd className="font-display text-title">{value}</dd>
    </div>
  );
}
